// Telas fora das páginas: início, configurações e entrada na conta.

import * as store from "./store.js";
import * as router from "./router.js";
import { api, configure } from "./api.js";
import { ui } from "./icons.js";
import { el, icon, titleOf, relTime, toast } from "./dom.js";
import { newPage, newDatabase } from "./sidebar.js";
import { quickSearch, pathLabel } from "./dialogs.js";
import * as updates from "./updates.js";
import { avatar } from "./people.js";
import { adjustAndUpload, imageFromTransfer } from "./image-adjust.js";

// ---------- Início ----------

export function renderHome(host) {
  const hour = new Date().getHours();
  const hello = hour < 12 ? "Bom dia" : hour < 18 ? "Boa tarde" : "Boa noite";
  const name = (store.state.me?.name || "").split(" ")[0];
  const recent = store.recent().slice(0, 8);
  const updated = [...store.state.tree.values()].sort((a, b) => b.updated_at - a.updated_at).filter((p) => !recent.includes(p)).slice(0, 8);
  const card = (p) => el("button", { class: "home-card", onclick: () => router.openPage(p.id) },
    el("span", { class: "home-card-icon" }, icon(p)),
    el("span", { class: "home-card-title" }, titleOf(p)),
    el("span", { class: "home-card-meta" }, pathLabel(p.id) || relTime(p.updated_at)));
  const q = el("input", { placeholder: "Pergunte qualquer coisa sobre as páginas, tarefas e reuniões (ex.: o que está bloqueado no DEV?)" });
  host.replaceChildren(el("div", { class: "home" },
    el("h1", { class: "home-hello" }, `${hello}${name ? ", " + name : ""}`),
    el("div", { class: "home-actions" },
      el("button", { class: "home-action", onclick: () => newPage() }, ui("page", 18), "Nova página"),
      el("button", { class: "home-action", onclick: () => newDatabase() }, ui("database", 18), "Novo banco de dados"),
      el("button", { class: "home-action", onclick: () => router.go({ view: "tasks" }) }, ui("tasks", 18), "Minhas tarefas"),
      el("button", { class: "home-action", onclick: () => router.go({ view: "inbox" }) }, ui("inbox", 18), "Caixa de entrada", store.state.unread ? el("span", { class: "nav-count" }, String(store.state.unread)) : null),
      el("button", { class: "home-action", onclick: () => quickSearch() }, ui("search", 18), "Buscar", el("kbd", {}, "Ctrl K")),
      el("button", { class: "home-action", onclick: () => import("./importer.js").then((m) => m.importDialog()) }, ui("import", 18), "Importar do Notion")),
    recent.length ? el("section", {}, el("h2", {}, "Visitadas recentemente"), el("div", { class: "home-grid" }, recent.map(card))) : null,
    updated.length ? el("section", {}, el("h2", {}, "Atualizadas recentemente"), el("div", { class: "home-grid" }, updated.map(card))) : null,
    !store.state.tree.size ? el("section", { class: "home-empty" },
      el("p", {}, "Seu espaço está vazio. Crie uma página, um banco de dados (board), ou importe tudo do Notion."),
      el("p", { class: "muted small" }, "Dica: dentro de qualquer página, digite \"/\" para inserir blocos, inclusive uma transcrição de reunião.")) : null,
    el("section", {}, el("h2", {}, "Perguntar ao Ordo"),
      el("form", { class: "ask", onsubmit: (e) => {
        e.preventDefault();
        const question = q.value.trim();
        if (question) router.go({ view: "ai", q: question });
      } }, q, el("button", { type: "submit" }, ui("ai", 14), " Perguntar"),
        el("button", { type: "button", title: "Conversar por voz", onclick: () => router.go({ view: "ai", voice: true }) }, ui("voice", 14), " Falar")))));
}

// ---------- Configurações ----------

const CHECKS = [
  ["autoDetect", "Detectar chamadas (Teams, Zoom, Meet, WhatsApp…) e oferecer a transcrição no pop-up"],
  ["autoStart", "Começar a transcrever sozinho quando detectar uma chamada"],
  ["floatingWidget", "Mostrar o pop-up flutuante durante as chamadas"],
  ["openAtLogin", "Abrir o Ordo ao ligar o computador (fica na bandeja)"],
];

export async function renderSettings(host, { settings, onSettings, onSignOut, onToken, info }) {
  const me = store.state.me || {};
  const acc = profileSection({ onToken });

  // Transcrição só para administradores; na versão web não há bandeja nem detecção de chamadas.
  const admin = me.role === "admin";
  const web = !!window.ordo.web;
  const checks = web ? [] : CHECKS.filter(([k]) => admin || k === "openAtLogin");
  const prefStatus = el("span", { class: "muted small" });
  const pref = el("form", { class: "form", onchange: async (e) => {
    const f = e.currentTarget;
    const patch = admin ? { language: f.language.value } : {};
    for (const [k] of checks) patch[k] = f[k].checked;
    onSettings(await window.ordo.saveSettings(patch));
    prefStatus.textContent = "Salvo.";
  } },
  checks.map(([k, label]) => el("label", { class: "check" }, el("input", { type: "checkbox", name: k, checked: !!settings[k] }), label)),
  !admin ? null : el("label", {}, "Idioma das chamadas", el("select", { name: "language" },
    [["pt", "Português"], ["en", "Inglês"], ["es", "Espanhol"], ["", "Detectar automaticamente"]].map(([v, l]) => el("option", { value: v, selected: settings.language === v }, l)))),
  prefStatus,
  !admin || info.detectionSupported ? null : el("p", { class: "muted small" }, web
    ? "Na versão web, use o bloco \"Transcrição de reunião\" (digite / numa página). Para gravar o som da chamada, compartilhe a aba ou a tela com o áudio ligado quando o navegador pedir."
    : "A detecção automática de chamadas funciona no Windows. Aqui, use o bloco \"Transcrição de reunião\" (digite / numa página)."));

  host.replaceChildren(el("div", { class: "settings" },
    el("h1", {}, "Configurações"),
    el("section", {}, el("h2", {}, "Seu perfil"), acc),
    admin || checks.length ? el("section", {}, el("h2", {}, admin ? "Transcrição de chamadas" : "Aplicativo"), pref) : null,
    el("section", {}, el("h2", {}, "Atalhos"), el("ul", { class: "shortcuts" },
      [["Ctrl+K", "Buscar"], ["Ctrl+N", "Nova página"], ["Ctrl+\\", "Mostrar/ocultar a barra lateral"], ["Alt+← / Alt+→", "Voltar / avançar"],
        ["/", "Inserir bloco (dentro do texto)"], ["Ctrl+Shift+P", "Pausar/retomar a transcrição"], ["Ctrl+Shift+O", "Encerrar a transcrição"]]
        .map(([k, d]) => el("li", {}, el("kbd", {}, k), d)))),
    me.role === "admin" ? el("section", {}, el("h2", {}, "Administração"),
      el("p", { class: "muted small" }, "Pessoas, papéis, acesso aos ambientes e o consumo da IA do app todo."),
      el("button", { onclick: () => router.go({ view: "admin" }) }, ui("admin", 14), " Abrir a administração")) : null,
    el("section", {}, el("h2", {}, "Atualizações"), updateSection(info)),
    el("section", {}, el("button", { class: "danger-btn", onclick: onSignOut }, "Sair da conta"))));
}

// Perfil: foto (com ajuste do enquadramento), nome, cargo, telefone, sobre,
// e-mail e senha. Cargo e foto aparecem para os outros no Compartilhar, nos
// membros dos ambientes, nos comentários e nas tarefas.
function profileSection({ onToken } = {}) {
  const me = store.state.me || {};
  const prof = me.profile || {};
  const status = el("span", { class: "muted small" });
  const photoBox = el("div", { class: "profile-photo", tabindex: 0, title: "Clique para trocar a foto, arraste uma imagem ou cole com Ctrl+V" });
  const fileInput = el("input", { type: "file", accept: "image/*", hidden: true });
  const removeBtn = el("button", { type: "button", class: "link small" }, "Remover foto");

  const save = async (patch, msg = "Perfil salvo.") => {
    try {
      store.state.me = { ...store.state.me, ...(await api("PATCH", "/me", patch)) };
      const u = (store.state.users || []).find((x) => x.id === store.state.me.id);
      if (u) Object.assign(u, { name: store.state.me.name, avatar: store.state.me.avatar, title: store.state.me.profile?.title || null });
      try { localStorage.setItem("ordo.me", JSON.stringify(store.state.me)); } catch { /* ignore */ }
      store.emit("tree");
      status.textContent = msg;
      drawPhoto();
      return true;
    } catch (err) {
      status.textContent = err.message;
      return false;
    }
  };
  const usePhoto = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) return toast("A imagem passa de 8 MB.");
    const url = await adjustAndUpload(file, { shape: "circle", size: 320, title: "Ajustar foto de perfil" });
    if (url) await save({ avatar: url }, "Foto atualizada.");
  };
  const drawPhoto = () => {
    photoBox.replaceChildren(avatar(store.state.me, "xl"), el("span", { class: "profile-photo-edit" }, ui("camera", 16)));
    removeBtn.hidden = !store.state.me?.avatar;
  };
  photoBox.addEventListener("click", () => fileInput.click());
  photoBox.addEventListener("keydown", (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); fileInput.click(); } });
  photoBox.addEventListener("paste", (e) => { const f = imageFromTransfer(e.clipboardData); if (f) { e.preventDefault(); usePhoto(f); } });
  photoBox.addEventListener("dragover", (e) => e.preventDefault());
  photoBox.addEventListener("drop", (e) => { e.preventDefault(); usePhoto(imageFromTransfer(e.dataTransfer)); });
  fileInput.addEventListener("change", () => { usePhoto(fileInput.files?.[0]); fileInput.value = ""; });
  removeBtn.addEventListener("click", () => save({ avatar: null }, "Foto removida."));
  drawPhoto();

  const field = (label, input, hint) => el("label", {}, label, input, hint ? el("span", { class: "muted small" }, hint) : null);
  const form = el("form", { class: "form profile-form", onsubmit: async (e) => {
    e.preventDefault();
    const f = e.target;
    const body = {
      name: f.name.value.trim(), email: f.email.value.trim() || undefined,
      profile: { title: f.title.value, phone: f.phone.value, bio: f.bio.value },
    };
    await save(body);
  } },
  el("div", { class: "profile-grid" },
    field("Nome", el("input", { name: "name", value: me.name || "", maxlength: 100, required: true })),
    field("Cargo ou função", el("input", { name: "title", value: prof.title || "", maxlength: 80, placeholder: "Ex.: Desenvolvedor, QA, Produto" })),
    field("E-mail", el("input", { name: "email", type: "email", value: me.email || "" }), "É com ele que você entra e que as pessoas te convidam."),
    field("Telefone", el("input", { name: "phone", type: "tel", value: prof.phone || "", maxlength: 40, placeholder: "Opcional" }))),
  field("Sobre você", el("textarea", { name: "bio", rows: 3, maxlength: 500, placeholder: "Uma frase sobre o que você faz (opcional)", value: prof.bio || "" })),
  el("div", { class: "row" }, el("button", { type: "submit", class: "primary" }, "Salvar perfil"), status));

  return el("div", { class: "profile" },
    el("div", { class: "profile-side" }, photoBox, fileInput,
      el("button", { type: "button", class: "small", onclick: () => fileInput.click() }, ui("upload", 13), " Trocar foto"),
      removeBtn,
      el("p", { class: "muted small profile-tip" }, "Dica: clique na foto e cole uma imagem com Ctrl+V.")),
    el("div", {}, form, passwordSection(me, onToken)));
}

// Trocar a senha: pede a senha atual. Quem não lembra pede um código por
// e-mail (o mesmo "Esqueci minha senha" da tela de entrada) e troca com ele;
// nesse caso todas as sessões são encerradas e o app entra de novo sozinho.
function passwordSection(me, onToken) {
  let byCode = false;
  const status = el("span", { class: "muted small pass-status" });
  const input = (name, autocomplete, placeholder) => el("input", { name, type: "password", autocomplete, minlength: 8, placeholder });
  const current = input("current", "current-password", "");
  const next = input("next", "new-password", "Pelo menos 8 caracteres");
  const again = input("again", "new-password", "");
  const code = el("input", { name: "code", inputmode: "numeric", autocomplete: "one-time-code", maxlength: 7, placeholder: "000000" });
  const field = (label, inp) => el("label", {}, label, inp);
  const currentField = field("Senha atual", current);
  const codeField = field("Código recebido por e-mail", code);
  codeField.hidden = true;
  const forgot = el("button", { type: "button", class: "link small", onclick: async () => {
    if (!me.email) return void (status.textContent = "Cadastre um e-mail no perfil primeiro.");
    status.textContent = "Mandando o código…";
    try {
      const { authClient, unwrap } = await import("./auth-client.js");
      await unwrap(authClient().emailOtp.requestPasswordReset({ email: me.email }));
      byCode = true;
      currentField.hidden = true;
      codeField.hidden = false;
      forgot.textContent = "Mandar outro código";
      status.textContent = `Mandamos um código para ${me.email}. Confira também a caixa de spam.`;
      code.focus();
    } catch (err) {
      status.textContent = err.message;
    }
  } }, "Esqueci a senha atual");
  const submit = el("button", { type: "button", class: "primary", onclick: async () => {
    status.textContent = "";
    if (next.value.length < 8) return void (status.textContent = "A senha nova precisa ter pelo menos 8 caracteres.");
    if (next.value !== again.value) return void (status.textContent = "As duas senhas novas não são iguais.");
    submit.disabled = true;
    try {
      if (byCode) {
        const { authClient, unwrap, tokenOf } = await import("./auth-client.js");
        const auth = authClient();
        const otp = code.value.replace(/\D/g, "");
        await unwrap(auth.emailOtp.resetPassword({ email: me.email, otp, password: next.value }));
        const token = tokenOf(await unwrap(auth.signIn.email({ email: me.email, password: next.value })));
        if (token && onToken) await onToken(token);
        toast("Senha trocada. As outras sessões foram encerradas.");
      } else {
        if (!current.value) throw new Error("Informe a senha atual.");
        await api("PATCH", "/me", { password: next.value, current_password: current.value });
        toast("Senha trocada.");
      }
      for (const i of [current, next, again, code]) i.value = "";
      byCode = false;
      currentField.hidden = false;
      codeField.hidden = true;
      forgot.textContent = "Esqueci a senha atual";
      status.textContent = "Senha trocada.";
    } catch (err) {
      status.textContent = err.message;
    } finally {
      submit.disabled = false;
    }
  } }, "Trocar a senha");
  return el("div", { class: "form profile-form" }, el("details", { class: "profile-pass" }, el("summary", {}, "Trocar a senha"),
    el("div", { class: "profile-grid" }, currentField, codeField, el("span"), field("Nova senha", next), field("Repita a nova senha", again)),
    el("div", { class: "row" }, submit, forgot), status));
}

function updateSection(info) {
  const text = el("p", { class: "muted small" });
  const checkBtn = el("button", { onclick: () => updates.check() }, "Procurar atualizações");
  const installBtn = el("button", { class: "primary", onclick: () => updates.install() }, "Reiniciar e atualizar");
  const box = el("div", { class: "update-section" },
    el("p", {}, `Você está usando o Ordo ${info.version}.`), text, el("div", { class: "row-actions" }, checkBtn, installBtn));
  const off = updates.onChange((s) => {
    if (!box.isConnected && box.dataset.mounted) return off();
    box.dataset.mounted = "1";
    text.textContent = updates.describe(s);
    checkBtn.hidden = s.status === "off" || s.status === "ready";
    checkBtn.disabled = s.status === "checking" || s.status === "downloading";
    installBtn.hidden = s.status !== "ready";
  });
  return box;
}

// ---------- Entrar / criar conta ----------

export function renderAuth(host, { settings, onToken, message }) {
  // login | register | reset. Contas pelo Better Auth: no cadastro (e ao entrar
  // com e-mail ainda não confirmado) e no "Esqueci minha senha" o servidor manda
  // um código de 6 dígitos por e-mail; o campo do código aparece depois.
  let mode = "login";
  let codeFor = null; // null | "verify" | "reset"
  const error = el("p", { class: "error" }, message || "");
  const info = el("p", { class: "muted small auth-info", hidden: true });
  const nameField = el("label", { hidden: true }, "Seu nome", el("input", { name: "name", autocomplete: "name" }));
  const passLabel = el("span", {}, "Senha");
  const passField = el("label", {}, passLabel, el("input", { name: "password", type: "password", minlength: 8, autocomplete: "current-password" }));
  const codeField = el("label", { hidden: true }, "Código recebido por e-mail",
    el("input", { name: "code", inputmode: "numeric", autocomplete: "one-time-code", maxlength: 7, placeholder: "000000" }));
  const resend = el("button", { type: "button", class: "link-btn small", hidden: true, onclick: () => resendCode() }, "Mandar outro código");
  const forgot = el("button", { type: "button", class: "link-btn small", onclick: () => setMode("reset") }, "Esqueci minha senha");
  const back = el("button", { type: "button", class: "link-btn small", hidden: true, onclick: () => setMode("login") }, "Voltar para entrar");
  const submit = el("button", { type: "submit", class: "primary wide" }, "Entrar");
  const tabs = el("div", { class: "auth-tabs" },
    ["login", "register"].map((m) => el("button", { type: "button", class: "auth-tab" + (m === mode ? " active" : ""), dataset: { mode: m }, onclick: () => setMode(m) }, m === "login" ? "Entrar" : "Criar conta")));
  const refresh = () => {
    for (const t of tabs.children) t.classList.toggle("active", t.dataset.mode === mode);
    tabs.hidden = mode === "reset";
    nameField.hidden = mode !== "register";
    codeField.hidden = !codeFor;
    resend.hidden = !codeFor;
    forgot.hidden = mode !== "login" || !!codeFor;
    back.hidden = mode !== "reset";
    // Na troca de senha, a senha nova só aparece junto com o código.
    passField.hidden = (mode === "reset" && !codeFor) || codeFor === "verify";
    passLabel.textContent = mode === "reset" ? "Nova senha" : "Senha";
    form.password.autocomplete = mode === "login" ? "current-password" : "new-password";
    submit.textContent = codeFor === "verify" ? "Confirmar e entrar"
      : mode === "login" ? "Entrar"
      : mode === "register" ? "Criar conta"
      : codeFor ? "Trocar a senha e entrar" : "Mandar código";
  };
  const setMode = (m) => {
    mode = m;
    codeFor = null;
    error.textContent = "";
    info.hidden = true;
    form.code.value = "";
    if (m === "reset") form.password.value = "";
    refresh();
  };
  const askedCode = (kind, email) => {
    codeFor = kind;
    info.textContent = kind === "verify"
      ? `Mandamos um código para ${email}. Confira também a caixa de spam.`
      : `Se ${email} tiver conta no Ordo, chega um código em instantes. Confira também a caixa de spam.`;
    info.hidden = false;
    refresh();
    requestAnimationFrame(() => form.code.focus());
  };
  const resendCode = async () => {
    error.textContent = "";
    try {
      const { authClient, unwrap } = await import("./auth-client.js");
      const email = form.email.value.trim();
      if (codeFor === "reset") await unwrap(authClient().emailOtp.requestPasswordReset({ email }));
      else await unwrap(authClient().emailOtp.sendVerificationOtp({ email, type: "email-verification" }));
      toast("Código reenviado.");
    } catch (err) {
      error.textContent = err.message;
    }
  };
  const form = el("form", { class: "form", onsubmit: async (e) => {
    e.preventDefault();
    error.textContent = "";
    submit.disabled = true;
    try {
      configure(settings);
      const { authClient, unwrap, tokenOf } = await import("./auth-client.js");
      const auth = authClient();
      const email = form.email.value.trim().toLowerCase();
      const password = form.password.value;
      const otp = form.code.value.replace(/\D/g, "");
      const needsPass = codeFor !== "verify" && !(mode === "reset" && !codeFor);
      if (needsPass && password.length < 8) throw new Error("A senha precisa ter pelo menos 8 caracteres.");
      let data;
      if (codeFor === "verify") {
        data = await unwrap(auth.emailOtp.verifyEmail({ email, otp }));
        if (!tokenOf(data)) data = await unwrap(auth.signIn.email({ email, password }));
      } else if (mode === "login") {
        try {
          data = await unwrap(auth.signIn.email({ email, password }));
        } catch (err) {
          if (err.code !== "EMAIL_NOT_VERIFIED") throw err;
          return void askedCode("verify", email);
        }
      } else if (mode === "register") {
        if (!form.name.value.trim()) throw new Error("Informe seu nome.");
        data = await unwrap(auth.signUp.email({ name: form.name.value.trim(), email, password }));
        if (!tokenOf(data)) return void askedCode("verify", email);
      } else if (!codeFor) {
        await unwrap(auth.emailOtp.requestPasswordReset({ email }));
        return void askedCode("reset", email);
      } else {
        await unwrap(auth.emailOtp.resetPassword({ email, otp, password }));
        data = await unwrap(auth.signIn.email({ email, password }));
      }
      const token = tokenOf(data);
      if (!token) throw new Error("Não foi possível entrar. Tente de novo.");
      await onToken(token);
    } catch (err) {
      error.textContent = err.message;
    } finally {
      submit.disabled = false;
    }
  } },
  tabs, nameField,
  el("label", {}, "E-mail", el("input", { name: "email", type: "email", required: true, autocomplete: "email",
    oninput: () => { if (codeFor) { codeFor = null; info.hidden = true; refresh(); } } })),
  passField, info, codeField,
  submit, error,
  el("div", { class: "auth-links" }, forgot, back, resend),
  el("details", { class: "auth-adv" }, el("summary", {}, "Avançado"),
    el("label", {}, "Token de acesso", el("input", { name: "token", placeholder: "Se você recebeu um token", onchange: async (e) => { if (e.target.value.trim()) await onToken(e.target.value.trim()); } }))));
  host.replaceChildren(el("div", { class: "auth" },
    el("div", { class: "auth-box" },
      el("div", { class: "auth-logo" }, el("img", { src: "logo.svg", alt: "", width: 56, height: 56 }), "Ordo"),
      el("p", { class: "muted" }, "Páginas, quadros e notas de reunião do seu time."),
      form)));
  refresh();
  requestAnimationFrame(() => form.email.focus());
}
